import { useCallback, useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import WCPDFDocument from "./WCPDFDocument";

const buttonClass = "pd-btn pd-btn--ghost";
const fileName = "wc_calculator_results.pdf";

const serialize = (value) => {
  if (value === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    console.warn("[wc] Failed to serialise data for PDF export", err);
    return null;
  }
};

export default function DownloadWCReportBtn({
  project,
  results,
  selfContainedRows,
  includeAccessible,
  entranceIdx,
  disabled,
}) {
  const serializedProject = useMemo(() => serialize(project), [project]);
  const serializedResults = useMemo(() => serialize(results), [results]);
  const serializedSelfContained = useMemo(() => serialize(selfContainedRows), [selfContainedRows]);

  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const hasResults = useMemo(
    () =>
      serializedResults &&
      typeof serializedResults === "object" &&
      Array.isArray(serializedResults.results) &&
      serializedResults.results.length > 0,
    [serializedResults],
  );

  const exportReady = useMemo(
    () => Boolean(serializedProject && serializedResults && serializedSelfContained && hasResults),
    [hasResults, serializedProject, serializedResults, serializedSelfContained],
  );

  useEffect(() => {
    if (exportReady) setError("");
  }, [exportReady]);

  const handleDownload = useCallback(async () => {
    if (!exportReady || disabled || isGenerating) return;

    setIsGenerating(true);
    setError("");

    try {
      const blob = await pdf(
        <WCPDFDocument
          project={serializedProject}
          results={serializedResults}
          selfContainedRows={serializedSelfContained}
          includeAccessible={includeAccessible}
          entranceIdx={entranceIdx}
        />,
        {},
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[wc] PDF export failed", err);
      setError(err?.message || "Export unavailable");
    } finally {
      setIsGenerating(false);
    }
  }, [
    disabled,
    entranceIdx,
    exportReady,
    includeAccessible,
    isGenerating,
    serializedProject,
    serializedResults,
    serializedSelfContained,
  ]);

  const disabledState = disabled || isGenerating || !exportReady;
  const title = error || (!exportReady ? "Run the calculator to export." : "");

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleDownload}
      disabled={disabledState}
      aria-disabled={disabledState}
      title={title || undefined}
    >
      {isGenerating ? "Generating…" : "Export PDF"}
    </button>
  );
}
